import React from "react";
import { Alert, Box, Button, CircularProgress, Typography, keyframes } from "@mui/material";
import {
  ArrowBack as BackIcon,
  CheckCircle as DoneIcon,
  RadioButtonUnchecked as PendingIcon,
  Replay as RetryIcon,
} from "@mui/icons-material";
import { HOL, holGhostBtnSx, holPrimaryBtnSx } from "../../../UserSide/pages/holTheme";
import { CardImage, Eyebrow, StepShell, holStageSx } from "./holHostUi";

const shuffle = keyframes`
  0%, 100% { transform: translateX(0) rotate(-6deg); }
  50% { transform: translateX(46px) rotate(8deg); }
`;

const shuffleBack = keyframes`
  0%, 100% { transform: translateX(0) rotate(6deg); }
  50% { transform: translateX(-46px) rotate(-8deg); }
`;

/**
 * `progress`: number of checklist items finished (0–3). `error` switches to the retry state.
 */
const HolStartingStep = ({ numberOfCards, progress = 0, error = null, onRetry, onBack }) => {
  const items = [
    "Creating the live session",
    `Shuffling a ${numberOfCards}-card deck`,
    "Opening the join queue for players",
  ];
  const allDone = progress >= items.length;

  return (
    <StepShell maxWidth={640}>
      <Box sx={{ ...holStageSx, p: { xs: 3, sm: 4.5 }, textAlign: "center" }}>
        <Eyebrow sx={{ mb: 2 }}>Step 3 · Start Session</Eyebrow>

        <Box aria-hidden sx={{ height: 150, display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
          <CardImage
            width={86}
            sx={{ animation: error ? "none" : `${shuffle} 1.1s ease-in-out infinite`, mr: -3 }}
          />
          <CardImage
            suit="heart"
            fileValue="1"
            width={86}
            sx={{ animation: error ? "none" : `${shuffleBack} 1.1s ease-in-out infinite` }}
          />
        </Box>

        <Typography
          sx={{
            fontFamily: HOL.fontDisplay,
            fontWeight: 900,
            fontSize: { xs: "1.6rem", sm: "2rem" },
            mb: 2.5,
            color: error ? HOL.lower : "#fff",
          }}
        >
          {error ? "Couldn't start the session" : allDone ? "You're live!" : "Starting the session…"}
        </Typography>

        <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, mx: "auto", maxWidth: 360, textAlign: "left", display: "grid", gap: 1.25 }}>
          {items.map((label, i) => {
            const done = i < progress;
            const active = i === progress && !error;
            return (
              <Box component="li" key={label} sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                {done ? (
                  <DoneIcon sx={{ color: HOL.higher, fontSize: 22 }} />
                ) : active ? (
                  <CircularProgress size={18} thickness={5} sx={{ color: HOL.gold, mx: "2px" }} />
                ) : (
                  <PendingIcon sx={{ color: "rgba(255,255,255,0.25)", fontSize: 22 }} />
                )}
                <Typography
                  sx={{
                    fontWeight: active ? 700 : 500,
                    color: done ? "#fff" : active ? HOL.gold : "rgba(255,255,255,0.45)",
                  }}
                >
                  {label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {error ? (
          <>
            <Alert severity="error" sx={{ mt: 3, textAlign: "left", borderRadius: "14px" }}>
              {error}
            </Alert>
            <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5, mt: 3, flexWrap: "wrap" }}>
              <Button variant="outlined" startIcon={<BackIcon />} onClick={onBack} sx={{ ...holGhostBtnSx, px: 3 }}>
                Back
              </Button>
              <Button variant="contained" startIcon={<RetryIcon />} onClick={onRetry} sx={holPrimaryBtnSx}>
                Try again
              </Button>
            </Box>
          </>
        ) : null}
      </Box>
    </StepShell>
  );
};

export default HolStartingStep;
